import { supabase } from '../lib/supabase';

export const knowledgeBaseService = {
  // Categories
  async getCategories() {
    const { data, error } = await supabase?.from('kb_categories')?.select('*')?.eq('is_active', true)?.order('sort_order', { ascending: true });

    if (error) throw error;

    return data?.map(cat => ({
      id: cat?.id,
      name: cat?.name,
      description: cat?.description,
      icon: cat?.icon,
      color: cat?.color,
      parentId: cat?.parent_id,
      sortOrder: cat?.sort_order,
      isActive: cat?.is_active,
      tenantId: cat?.tenant_id,
      createdAt: cat?.created_at,
      updatedAt: cat?.updated_at,
      createdBy: cat?.created_by
    }));
  },

  async createCategory(categoryData) {
    const { data, error } = await supabase?.from('kb_categories')?.insert({
        name: categoryData?.name,
        description: categoryData?.description,
        icon: categoryData?.icon,
        color: categoryData?.color,
        parent_id: categoryData?.parentId,
        sort_order: categoryData?.sortOrder,
        is_active: categoryData?.isActive ?? true
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      icon: data?.icon,
      color: data?.color,
      parentId: data?.parent_id,
      sortOrder: data?.sort_order,
      isActive: data?.is_active,
      createdAt: data?.created_at
    };
  },

  // Articles
  async getArticles(filters = {}) {
    let query = supabase?.from('kb_articles')?.select(`
        *,
        kb_categories(id, name, icon, color),
        kb_article_roles(role_id, can_view)
      `)?.eq('status', 'published');

    if (filters?.categoryId) {
      query = query?.eq('category_id', filters?.categoryId);
    }

    if (filters?.articleType) {
      query = query?.eq('article_type', filters?.articleType);
    }

    if (filters?.isFeatured !== undefined) {
      query = query?.eq('is_featured', filters?.isFeatured);
    }

    if (filters?.searchQuery) {
      query = query?.or(`title.ilike.%${filters?.searchQuery}%,content.ilike.%${filters?.searchQuery}%,tags.cs.{${filters?.searchQuery}}`);
    }

    query = query?.order('published_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(article => ({
      id: article?.id,
      title: article?.title,
      slug: article?.slug,
      content: article?.content,
      excerpt: article?.excerpt,
      articleType: article?.article_type,
      status: article?.status,
      categoryId: article?.category_id,
      category: article?.kb_categories ? {
        id: article?.kb_categories?.id,
        name: article?.kb_categories?.name,
        icon: article?.kb_categories?.icon,
        color: article?.kb_categories?.color
      } : null,
      viewCount: article?.view_count,
      isFeatured: article?.is_featured,
      tags: article?.tags || [],
      metadata: article?.metadata,
      createdAt: article?.created_at,
      updatedAt: article?.updated_at,
      publishedAt: article?.published_at,
      createdBy: article?.created_by,
      updatedBy: article?.updated_by,
      roles: article?.kb_article_roles?.map(r => ({
        roleId: r?.role_id,
        canView: r?.can_view
      })) || []
    }));
  },

  async getArticleBySlug(slug) {
    const { data, error } = await supabase?.from('kb_articles')?.select(`
        *,
        kb_categories(id, name, icon, color),
        kb_article_roles(role_id, can_view)
      `)?.eq('slug', slug)?.eq('status', 'published')?.single();

    if (error) throw error;

    // Increment view count
    await supabase?.from('kb_articles')?.update({ view_count: (data?.view_count || 0) + 1 })?.eq('id', data?.id);

    return {
      id: data?.id,
      title: data?.title,
      slug: data?.slug,
      content: data?.content,
      excerpt: data?.excerpt,
      articleType: data?.article_type,
      status: data?.status,
      categoryId: data?.category_id,
      category: data?.kb_categories ? {
        id: data?.kb_categories?.id,
        name: data?.kb_categories?.name,
        icon: data?.kb_categories?.icon,
        color: data?.kb_categories?.color
      } : null,
      viewCount: data?.view_count,
      isFeatured: data?.is_featured,
      tags: data?.tags || [],
      metadata: data?.metadata,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      publishedAt: data?.published_at,
      createdBy: data?.created_by,
      updatedBy: data?.updated_by,
      roles: data?.kb_article_roles?.map(r => ({
        roleId: r?.role_id,
        canView: r?.can_view
      })) || []
    };
  },

  async createArticle(articleData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('kb_articles')?.insert({
        title: articleData?.title,
        slug: articleData?.slug || articleData?.title?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-'),
        content: articleData?.content,
        excerpt: articleData?.excerpt,
        article_type: articleData?.articleType || 'guide',
        status: articleData?.status || 'draft',
        category_id: articleData?.categoryId,
        is_featured: articleData?.isFeatured || false,
        tags: articleData?.tags || [],
        metadata: articleData?.metadata || {},
        published_at: articleData?.status === 'published' ? new Date()?.toISOString() : null,
        created_by: user?.id,
        updated_by: user?.id
      })?.select()?.single();

    if (error) throw error;

    // Create role assignments
    if (articleData?.roleIds && articleData?.roleIds?.length > 0) {
      const roleAssignments = articleData?.roleIds?.map(roleId => ({
        article_id: data?.id,
        role_id: roleId,
        can_view: true
      }));

      await supabase?.from('kb_article_roles')?.insert(roleAssignments);
    }

    return {
      id: data?.id,
      title: data?.title,
      slug: data?.slug,
      content: data?.content,
      excerpt: data?.excerpt,
      articleType: data?.article_type,
      status: data?.status,
      categoryId: data?.category_id,
      isFeatured: data?.is_featured,
      tags: data?.tags,
      createdAt: data?.created_at
    };
  },

  async updateArticle(articleId, articleData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {
      updated_by: user?.id
    };

    if (articleData?.title !== undefined) updateData.title = articleData?.title;
    if (articleData?.slug !== undefined) updateData.slug = articleData?.slug;
    if (articleData?.content !== undefined) updateData.content = articleData?.content;
    if (articleData?.excerpt !== undefined) updateData.excerpt = articleData?.excerpt;
    if (articleData?.articleType !== undefined) updateData.article_type = articleData?.articleType;
    if (articleData?.status !== undefined) {
      updateData.status = articleData?.status;
      if (articleData?.status === 'published' && !articleData?.publishedAt) {
        updateData.published_at = new Date()?.toISOString();
      }
    }
    if (articleData?.categoryId !== undefined) updateData.category_id = articleData?.categoryId;
    if (articleData?.isFeatured !== undefined) updateData.is_featured = articleData?.isFeatured;
    if (articleData?.tags !== undefined) updateData.tags = articleData?.tags;
    if (articleData?.metadata !== undefined) updateData.metadata = articleData?.metadata;

    const { data, error } = await supabase?.from('kb_articles')?.update(updateData)?.eq('id', articleId)?.select()?.single();

    if (error) throw error;

    // Update role assignments if provided
    if (articleData?.roleIds !== undefined) {
      // Delete existing assignments
      await supabase?.from('kb_article_roles')?.delete()?.eq('article_id', articleId);

      // Create new assignments
      if (articleData?.roleIds?.length > 0) {
        const roleAssignments = articleData?.roleIds?.map(roleId => ({
          article_id: articleId,
          role_id: roleId,
          can_view: true
        }));

        await supabase?.from('kb_article_roles')?.insert(roleAssignments);
      }
    }

    return {
      id: data?.id,
      title: data?.title,
      slug: data?.slug,
      content: data?.content,
      excerpt: data?.excerpt,
      articleType: data?.article_type,
      status: data?.status,
      categoryId: data?.category_id,
      isFeatured: data?.is_featured,
      tags: data?.tags,
      updatedAt: data?.updated_at
    };
  },

  async deleteArticle(articleId) {
    const { error } = await supabase?.from('kb_articles')?.delete()?.eq('id', articleId);

    if (error) throw error;
  },

  // Search
  async searchArticles(searchQuery) {
    const { data, error } = await supabase?.from('kb_articles')?.select(`
        *,
        kb_categories(id, name, icon, color)
      `)?.eq('status', 'published')?.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%,tags.cs.{${searchQuery}}`)?.order('view_count', { ascending: false })?.limit(10);

    if (error) throw error;

    return data?.map(article => ({
      id: article?.id,
      title: article?.title,
      slug: article?.slug,
      excerpt: article?.excerpt,
      category: article?.kb_categories ? {
        id: article?.kb_categories?.id,
        name: article?.kb_categories?.name,
        icon: article?.kb_categories?.icon,
        color: article?.kb_categories?.color
      } : null,
      viewCount: article?.view_count,
      tags: article?.tags || []
    }));
  },

  // Get popular articles
  async getPopularArticles(limit = 5) {
    const { data, error } = await supabase?.from('kb_articles')?.select(`
        *,
        kb_categories(id, name, icon, color)
      `)?.eq('status', 'published')?.order('view_count', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(article => ({
      id: article?.id,
      title: article?.title,
      slug: article?.slug,
      excerpt: article?.excerpt,
      category: article?.kb_categories ? {
        id: article?.kb_categories?.id,
        name: article?.kb_categories?.name,
        icon: article?.kb_categories?.icon,
        color: article?.kb_categories?.color
      } : null,
      viewCount: article?.view_count,
      tags: article?.tags || []
    }));
  }
};